import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  Observable,
  skip,
} from 'rxjs';
import {isEqual} from 'lodash';
import {Visibility, TriggerParams, WidgetVisibilityAPI} from '.';
import {
  MessageFromParentType,
  ParentMessageBus,
  ParentMessageListenerFn,
  requestVisibilityChange,
  sendWidgetInitialized,
} from '../iframe';

export class DefaultVisibilityController implements WidgetVisibilityAPI {
  private visibility$: BehaviorSubject<Visibility>;

  private triggerParams$: BehaviorSubject<TriggerParams | null>;

  constructor(initialVisibility: Visibility = 'closed') {
    this.visibility$ = new BehaviorSubject<Visibility>(initialVisibility);
    this.triggerParams$ = new BehaviorSubject<TriggerParams | null>(null);
  }

  visibility = (): Visibility => {
    return this.visibility$.value;
  };

  visibilityObservable = (): Observable<Visibility> => {
    return this.visibility$.asObservable().pipe(
      // only emit distinct changes; no back-to-back repeats
      distinctUntilChanged(),
      // skip(1) skips initial value; only emit changes
      skip(1),
    );
  };

  triggerParams = (): TriggerParams | null => {
    return this.triggerParams$.value;
  };

  triggerParamsObservable = (): Observable<TriggerParams> => {
    return this.triggerParams$.asObservable().pipe(
      // only emit distinct changes; no back-to-back repeats
      distinctUntilChanged((prev, next) => isEqual(prev, next)),
      // skip(1) skips initial value; only emit changes
      skip(1),
      // only emit non-null values
      // 2 identical params will emit if the params are cleared
      // in between (set to null)
      filter((params): params is TriggerParams => !!params),
    );
  };

  setTriggerParams(triggerParams: TriggerParams) {
    this.triggerParams$.next(triggerParams);
  }

  open = (): void => {
    this.visibility$.next('open');
  };

  close = (): void => {
    this.visibility$.next('closed');
  };

  minimize = (): void => {
    this.visibility$.next('minimized');
  };

  toggle = (): void => {
    if (this.visibility() === 'open') {
      this.minimize();
    } else {
      this.open();
    }
  };
}

export class ParentVisibilityController implements WidgetVisibilityAPI {
  private visibility$: BehaviorSubject<Visibility>;

  private triggerParams$: BehaviorSubject<TriggerParams | null>;

  private parentBus: ParentMessageBus;

  private parentHandler: ParentMessageListenerFn | null = null;

  constructor({
    initialVisibility = 'closed',
    parentBus,
  }: {
    initialVisibility?: Visibility;
    parentBus: ParentMessageBus;
  }) {
    this.parentBus = parentBus;
    this.visibility$ = new BehaviorSubject<Visibility>(initialVisibility);
    this.triggerParams$ = new BehaviorSubject<TriggerParams | null>(null);
    this.listenToParent();
  }

  visibility = (): Visibility => {
    return this.visibility$.value;
  };

  visibilityObservable = (): Observable<Visibility> => {
    return this.visibility$.asObservable().pipe(
      // only emit distinct changes; no back-to-back repeats
      distinctUntilChanged(),
      // skip(1) skips initial value; only emit changes
      skip(1),
    );
  };

  triggerParams = (): TriggerParams | null => {
    return this.triggerParams$.value;
  };

  triggerParamsObservable = (): Observable<TriggerParams> => {
    return this.triggerParams$.asObservable().pipe(
      // only emit distinct changes; no back-to-back repeats
      distinctUntilChanged((prev, next) => isEqual(prev, next)),
      // skip(1) skips initial value; only emit changes
      skip(1),
      // only emit non-null values
      // 2 identical params will emit if the params are cleared
      // in between (set to null)
      filter((params): params is TriggerParams => !!params),
    );
  };

  setTriggerParams(triggerParams: TriggerParams | null) {
    this.triggerParams$.next(triggerParams);
  }

  open = (): void => {
    requestVisibilityChange(this.parentBus, 'open');
  };

  close = (): void => {
    requestVisibilityChange(this.parentBus, 'closed');
  };

  minimize = (): void => {
    requestVisibilityChange(this.parentBus, 'minimized');
  };

  toggle = (): void => {
    if (this.visibility() === 'open') {
      this.minimize();
    } else {
      this.open();
    }
  };

  listenToParent = (): void => {
    if (this.parentHandler) {
      return;
    }

    this.parentHandler = (message: any) => {
      try {
        if (
          message.type === MessageFromParentType.VisibilityChange &&
          ['open', 'closed', 'minimized'].includes(message.data?.visibility)
        ) {
          this.triggerParams$.next(message.data.triggerParams);
          this.visibility$.next(message.data.visibility);
        }

        if (message.type === MessageFromParentType.CheckInitialized) {
          sendWidgetInitialized(this.parentBus);
          requestVisibilityChange(this.parentBus, this.visibility());
        }
      } catch {}
    };
    this.parentBus.addListener(this.parentHandler);
  };

  stopListeningToParent = (): void => {
    if (!this.parentHandler) {
      return;
    }

    this.parentBus.removeListener(this.parentHandler);
    this.parentHandler = null;
  };
}
